/**
 * TimeEntryList — Renders a list of time entries with full detail.
 * Sprint 5 + Sprint 10 (edit/void/audit actions), SDD §7.3
 *
 * Displays clock-in/out times, duration, category, position, apparatus,
 * shift type. Visually distinguishes officer-submitted entries.
 * Sprint 10: Adds edit, void, and audit history action buttons.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRolePriority } from '@shared/roles';
import EditEntryModal from './EditEntryModal';
import AuditHistory from './AuditHistory';
import { apiFetch } from '../lib/api';

export interface TimeEntry {
  id: number;
  category: string;
  position: string | null;
  apparatus: string | null;
  clock_in: string;
  clock_out: string | null;
  duration_minutes: number | null;
  duty_battalion_id: number | null;
  shift_type: string;
  notes: string | null;
  source: string;
  submitted_by: number | null;
  created_at: string;
  updated_at: string;
}

interface TimeEntryListProps {
  entries: TimeEntry[];
  loading?: boolean;
  emptyMessage?: string;
  userRole?: string;
  onEntryUpdated?: (updated: TimeEntry) => void;
  onEntryVoided?: (entryId: number) => void;
}

// ── Formatting helpers ──────────────────────────────────────────────────

function formatDuration(minutes: number | null): string {
  if (minutes === null) return '—';
  if (minutes === 0) return '0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function formatTime(isoTimestamp: string): string {
  return new Date(isoTimestamp).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatDate(isoTimestamp: string): string {
  return new Date(isoTimestamp).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatCategory(category: string): string {
  return category
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatPosition(position: string): string {
  return position
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function isOfficerSubmitted(entry: TimeEntry): boolean {
  return entry.source === 'officer' || entry.source === 'admin';
}

function isGeoAutoEntry(entry: TimeEntry): boolean {
  return entry.source === 'geo_auto';
}

/**
 * Check if the 24h member edit window is still open (client-side check).
 * Mirrors canMemberEditEntry in the worker.
 */
function isMemberEditWindowOpen(entry: TimeEntry): boolean {
  if (!entry.clock_out) return true;
  const clockOutMs = new Date(entry.clock_out).getTime();
  const twentyFourHoursMs = 24 * 60 * 60 * 1000;
  return Date.now() - clockOutMs <= twentyFourHoursMs;
}

// ── Component ───────────────────────────────────────────────────────────

export default function TimeEntryList({
  entries,
  loading = false,
  emptyMessage = 'No entries found.',
  userRole = 'member',
  onEntryUpdated,
  onEntryVoided,
}: TimeEntryListProps) {
  if (loading) {
    return <div className="py-10 text-center text-content-tertiary text-sm">Loading entries...</div>;
  }

  if (entries.length === 0) {
    return <div className="py-10 text-center text-content-tertiary text-sm">{emptyMessage}</div>;
  }

  return (
    <div className="flex flex-col gap-2">
      {entries.map((entry) => (
        <TimeEntryCard
          key={entry.id}
          entry={entry}
          userRole={userRole}
          onEntryUpdated={onEntryUpdated}
          onEntryVoided={onEntryVoided}
        />
      ))}
    </div>
  );
}

function TimeEntryCard({
  entry,
  userRole,
  onEntryUpdated,
  onEntryVoided,
}: {
  entry: TimeEntry;
  userRole: string;
  onEntryUpdated?: (updated: TimeEntry) => void;
  onEntryVoided?: (entryId: number) => void;
}) {
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);

  const officerEntry = isOfficerSubmitted(entry);
  const geoAutoEntry = isGeoAutoEntry(entry);
  const timeRange = entry.clock_out
    ? `${formatTime(entry.clock_in)} – ${formatTime(entry.clock_out)}`
    : `${formatTime(entry.clock_in)} – now`;

  // Determine if user can edit this entry
  const isAdminOrOfficer = getRolePriority(userRole) >= 50;
  const isAdmin = userRole === 'admin';
  const canEdit = isAdminOrOfficer || isMemberEditWindowOpen(entry);
  const canVoid = isAdminOrOfficer;

  async function handleVoidConfirmed() {
    setVoidConfirmOpen(false);
    setVoidError(null);
    setVoiding(true);
    try {
      await apiFetch(`/api/admin/punch/${entry.id}`, { method: 'DELETE' });
      onEntryVoided?.(entry.id);
    } catch {
      setVoidError('Failed to void entry.');
    } finally {
      setVoiding(false);
    }
  }

  return (
    <>
      <div
        className={`px-3.5 py-3 bg-surface-raised rounded-lg ${
          geoAutoEntry
            ? 'border border-teal-500/40'
            : officerEntry
              ? 'border border-action-primary'
              : 'border border-edge-muted'
        }`}
      >
        {/* Row 1: Date + Time range */}
        <div className="flex justify-between items-baseline">
          <span className="text-sm font-semibold text-content-primary">{formatDate(entry.clock_in)}</span>
          <span className="text-[13px] text-content-tertiary">{timeRange}</span>
        </div>

        {/* Row 2: Category · Position · Apparatus + Duration */}
        <div className="flex justify-between items-baseline mt-1">
          <span className="text-[13px] text-content-secondary">
            {formatCategory(entry.category)}
            {entry.position && ` · ${formatPosition(entry.position)}`}
            {entry.apparatus && ` · ${entry.apparatus}`}
          </span>
          <span className="text-[13px] font-semibold text-action-primary">
            {formatDuration(entry.duration_minutes)}
          </span>
        </div>

        {/* Row 3 (optional): Shift type + officer badge */}
        {(entry.shift_type !== 'scheduled' || officerEntry || geoAutoEntry || entry.notes) && (
          <div className="flex gap-2 items-center mt-1.5 flex-wrap">
            {entry.shift_type !== 'scheduled' && (
              <span className="text-[11px] font-semibold text-content-secondary bg-surface-sunken px-1.5 py-0.5 rounded uppercase tracking-wider">
                {entry.shift_type}
              </span>
            )}
            {officerEntry && (
              <span className="text-[11px] font-semibold text-action-primary bg-action-primary/15 px-1.5 py-0.5 rounded uppercase tracking-wider">
                {entry.source === 'officer' ? 'Officer Entry' : 'Admin Entry'}
              </span>
            )}
            {geoAutoEntry && (
              <span className="text-[11px] font-semibold text-teal-400 bg-teal-400/15 px-1.5 py-0.5 rounded uppercase tracking-wider">
                Auto-Tracked
              </span>
            )}
            {entry.notes && (
              <span
                className="text-xs text-content-tertiary italic overflow-hidden text-ellipsis whitespace-nowrap max-w-[200px]"
                title={entry.notes}
              >
                {entry.notes}
              </span>
            )}
          </div>
        )}

        {/* Row 4: Action buttons (Sprint 10) */}
        <div className="flex gap-1.5 mt-2 pt-2 border-t border-edge-muted">
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="px-2 py-0.5 text-[11px] font-semibold rounded border border-edge-default bg-transparent text-content-secondary cursor-pointer tracking-wide"
            >
              Edit
            </button>
          )}
          {canVoid && (
            <button
              type="button"
              onClick={() => setVoidConfirmOpen(true)}
              disabled={voiding}
              className={`px-2 py-0.5 text-[11px] font-semibold rounded border border-danger-500/30 bg-transparent text-danger-500 cursor-pointer tracking-wide ${
                voiding ? 'opacity-50' : ''
              }`}
            >
              {voiding ? 'Voiding...' : 'Void'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setAuditOpen(true)}
            className="px-2 py-0.5 text-[11px] font-semibold rounded border border-edge-default bg-transparent text-content-tertiary cursor-pointer tracking-wide"
          >
            History
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={() => navigate(`/admin/entry/${entry.id}`)}
              className="px-2 py-0.5 text-[11px] font-semibold rounded border border-edge-default bg-transparent text-content-tertiary cursor-pointer tracking-wide"
            >
              View Detail
            </button>
          )}
        </div>
      </div>

      {/* Void error banner */}
      {voidError && (
        <div className="px-3 py-2 mt-1 bg-danger-500/15 border border-danger-500/30 rounded-md text-danger-400 text-xs flex justify-between items-center">
          <span>{voidError}</span>
          <button
            type="button"
            onClick={() => setVoidError(null)}
            className="bg-transparent border-none text-danger-400 cursor-pointer text-sm"
          >
            ✕
          </button>
        </div>
      )}

      {/* Void confirmation dialog */}
      {voidConfirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Confirm void entry"
          className="fixed inset-0 flex items-center justify-center bg-black/70 z-[1000] p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) setVoidConfirmOpen(false);
          }}
        >
          <div className="w-full max-w-[340px] bg-surface-raised rounded-2xl border border-edge-default p-6 shadow-2xl text-center">
            <h3 className="text-base font-bold text-content-primary mb-2">Void Entry?</h3>
            <p className="text-[13px] text-content-secondary mb-5">
              Are you sure you want to void this entry? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setVoidConfirmOpen(false)}
                className="flex-1 py-3 text-sm font-semibold rounded-lg border border-edge-default bg-transparent text-content-secondary cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleVoidConfirmed}
                className="flex-1 py-3 text-sm font-bold rounded-lg border-none bg-danger-500 text-white cursor-pointer"
              >
                Void Entry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editOpen && (
        <EditEntryModal
          entry={entry}
          userRole={userRole}
          onSave={(updated) => {
            setEditOpen(false);
            onEntryUpdated?.(updated);
          }}
          onClose={() => setEditOpen(false)}
        />
      )}

      {/* Audit history modal */}
      {auditOpen && <AuditHistory entryId={entry.id} onClose={() => setAuditOpen(false)} />}
    </>
  );
}
